/**
 * Primitive Color Tokens (Internal Design System)
 *
 * This is the SINGLE SOURCE OF TRUTH for all colors in the internal design system.
 * All colors use a 50-900 scale (Tailwind-style).
 *
 * Usage:
 *   import { colors } from '~/tokens/internal'
 *   colors.blue[500] // '#067EB1'
 */

const coralScale = {
  50: '#FFF2F0',
  100: '#FFDDD4',
  200: '#FFBBA8',
  300: '#FF886A',
  400: '#F96C46',
  500: '#EB542A',
  600: '#DB4D26',
  700: '#BE4827',
  800: '#A0391D',
  900: '#7F270E',
} as const

const tealScale = {
  50: '#E9FBF9',
  100: '#C7F4EE',
  200: '#9DE7DD',
  300: '#66D6C7',
  400: '#3BCEBA',
  500: '#26B5A2',
  600: '#199A88',
  700: '#107F70',
  800: '#0A665A',
  900: '#064B42',
} as const

const cyanScale = {
  50: '#EAF7FD',
  100: '#C2E8F8',
  200: '#90D4F2',
  300: '#55BCE8',
  400: '#2FA6DE',
  500: '#1A8ABE',
  600: '#126E9C',
  700: '#0C527A',
  800: '#073858',
  900: '#031E32',
} as const

const primaryScale = {
  50: '#DBE5F5',
  100: '#B8C8E6',
  200: '#84A0D0',
  300: '#5078B4',
  400: '#285690',
  500: '#0E3A6A',
  600: '#05224C',
  700: '#01122E',
  800: '#010A1C',
  900: '#00040C',
} as const

const navyScale = {
  50: '#EEF2FA',
  100: '#CDDAF4',
  200: '#A8BFEC',
  300: '#7599DE',
  400: '#4D74CC',
  500: '#3358B8',
  600: '#244BAA',
  700: '#1B3A9E',
  800: '#102878',
  900: '#091850',
} as const

const midnightScale = {
  50: '#E8EBF5',
  100: '#BEC8EA',
  200: '#8E9ED8',
  300: '#6078C0',
  400: '#3A529E',
  500: '#1E3470',
  600: '#101E52',
  700: '#06103A',
  800: '#030924',
  900: '#010412',
} as const

const neutralScale = {
  0: '#FFFFFF',
  50: '#FAFAFA',
  100: '#F2F2F3',
  200: '#E5E5E8',
  300: '#CECDD2',
  400: '#A8A8AF',
  500: '#80808A',
  600: '#5E5E68',
  700: '#474E5E',
  800: '#2E3240',
  900: '#181B24',
  925: '#232323',
  950: '#1e1e1e',
  975: '#161616',
  1000: '#121212',
} as const

export const colors = {
  // === BRAND ===
  primary: {
    50: primaryScale[50],
    100: primaryScale[100],
    200: primaryScale[200],
    300: primaryScale[300],
    400: primaryScale[400],
    500: primaryScale[500],
    600: primaryScale[600],
    700: primaryScale[700],
    800: primaryScale[800],
    900: primaryScale[900],
  },

  secondary: {
    50: cyanScale[50],
    100: cyanScale[100],
    200: cyanScale[200],
    300: cyanScale[300],
    400: cyanScale[400],
    500: cyanScale[500],
    600: cyanScale[600],
    700: cyanScale[700],
    800: cyanScale[800],
    900: cyanScale[900],
  },

  coral: {
    50: coralScale[50],
    100: coralScale[100],
    200: coralScale[200],
    300: coralScale[300],
    400: coralScale[400],
    500: coralScale[500],
    600: coralScale[600],
    700: coralScale[700],
    800: coralScale[800],
    900: coralScale[900],
  },

  // === SEMANTIC ===
  red: {
    50: '#FFECEC',
    100: '#F8C4C4',
    200: '#F19898',
    300: '#E15D5D',
    400: '#D83C3C',
    500: '#BF2626',
    600: '#AA1818',
    700: '#8F0202',
    800: '#6E0000',
    900: '#600000',
  },
  orange: {
    50: '#FFF1E4',
    100: '#FFD9B5',
    200: '#FAC390',
    300: '#F2AC6A',
    400: '#E18D3F',
    500: '#CC7523',
    600: '#AF5C0E',
    700: '#994E09',
    800: '#7B3F07',
    900: '#623206',
  },
  green: {
    50: '#ECFAED',
    100: '#C2ECD0',
    200: '#9FCFAF',
    300: '#71B588',
    400: '#509D6A',
    500: '#3D8A57',
    600: '#2A7042',
    700: '#1A592F',
    800: '#114D25',
    900: '#0B401C',
  },
  blue: {
    50: '#E9F3FF',
    100: '#C4DDF8',
    200: '#98C7F1',
    300: '#5DA5E1',
    400: '#3C92D8',
    500: '#267ABF',
    600: '#1868AA',
    700: '#02528F',
    800: '#003E6E',
    900: '#003260',
  },

  // === NEUTRALS ===
  neutral: neutralScale,

  // === ACCENTS ===

  amber: {
    50: '#fff9e5',
    100: '#fff2ca',
    200: '#ffe1a1',
    300: '#f7c677',
    400: '#f2b451',
    500: '#dd9d37',
    600: '#c68121',
    700: '#b25f12',
    800: '#9a510e',
    900: '#823c05',
  },

  yellow: {
    50: '#FFFCED',
    100: '#F6EED7',
    200: '#DFD197',
    300: '#C5B27E',
    400: '#AD975B',
    500: '#8E7B3A',
    600: '#816824',
    700: '#6F5314',
    800: '#60480C',
    900: '#4C3905',
  },
  cyan: cyanScale,

  teal: tealScale,
  purple: {
    50: '#F9EDFF',
    100: '#EAD7F6',
    200: '#C5A5D9',
    300: '#A97EC5',
    400: '#8C5BAD',
    500: '#6C3A8E',
    600: '#582481',
    700: '#48146F',
    800: '#3C0C60',
    900: '#2D054C',
  },
  deepPurple: {
    50: '#F3EDFF',
    100: '#E1D7F6',
    200: '#BAA5D9',
    300: '#9A7EC5',
    400: '#7C5BAD',
    500: '#5C3A8E',
    600: '#4E2481',
    700: '#3C146F',
    800: '#2C0757',
    900: '#210145',
  },

  indigo: {
    25: '#F6F5FC',
    50: '#E9ECFB',
    100: '#C7CEF5',
    200: '#99A7EA',
    300: '#6276DA',
    400: '#3E55CC',
    500: '#2C42AF',
    600: '#203392',
    700: '#152679',
    800: '#0F1C62',
    900: '#091449',
  },
  pink: {
    50: '#FCE8F1',
    100: '#F6C5DB',
    200: '#EB98BE',
    300: '#DC6098',
    400: '#CE3B7D',
    500: '#B22A67',
    600: '#941E53',
    700: '#7B1442',
    800: '#630D34',
    900: '#490826',
  },
  lime: {
    50: '#F4FAE6',
    100: '#DCF0AA',
    200: '#C2E472',
    300: '#A5D143',
    400: '#87B828',
    500: '#6A9A18',
    600: '#527B10',
    700: '#3C5E0A',
    800: '#274005',
    900: '#152302',
  },

  midnight: midnightScale,

  navy: navyScale,

  sky: {
    50: '#EAF3FA',
    100: '#C8DDE9',
    200: '#8FB3D0',
    300: '#5B8DB8',
    400: '#3E72A0',
    500: '#2E6A99',
    600: '#21527A',
    700: '#173B5C',
    800: '#0E2740',
    900: '#071525',
  },

  fern: {
    50: '#EBF3EE',
    100: '#BDD8C4',
    200: '#8FBC99',
    300: '#78A887',
    400: '#5D9170',
    500: '#4A7A5A',
    600: '#396348',
    700: '#2B5240',
    800: '#1C3829',
    900: '#0F2018',
  },

  plum: {
    50: '#F0EDF8',
    100: '#D4C2E8',
    200: '#B89DD5',
    300: '#9B7FBA',
    400: '#8163A2',
    500: '#6B4F8A',
    600: '#543D70',
    700: '#3D2B5C',
    800: '#281C3E',
    900: '#160F22',
  },

  terra: {
    50: '#FAF0EF',
    100: '#EEC9C5',
    200: '#DA9189',
    300: '#C4655A',
    400: '#A84D43',
    500: '#8E3A32',
    600: '#7A3028',
    700: '#5C211B',
    800: '#3D1410',
    900: '#220B09',
  },

  gold: {
    50: '#FBF6E9',
    100: '#F0DFB0',
    200: '#DFC078',
    300: '#C9A84C',
    400: '#A88A33',
    500: '#8A6F24',
    600: '#7A6020',
    700: '#5C4716',
    800: '#3C2E0D',
    900: '#211907',
  },

  gray: {
    50: '#F5F6F7',
    100: '#E2E6EC',
    200: '#B8C6D6',
    300: '#8DA5B8',
    400: '#6B85A0',
    500: '#506A82',
    600: '#3D5166',
    700: '#2A3A4A',
    800: '#1A2633',
    900: '#0D141C',
  },

  violet: {
    50: '#F3EFFE',
    100: '#DDD0FA',
    200: '#C4ADF5',
    300: '#A885EF',
    400: '#8F63E0',
    500: '#7448CC',
    600: '#5C35AD',
    700: '#452588',
    800: '#2E185F',
    900: '#190D38',
  },

  // Additional utility colors
  focus: {
    50: '#F0FAFF',
    100: '#D6F0FF',
    200: '#BCE6FF',
    300: '#A2DCFF',
    400: '#98E0FF',
    500: '#98E0FF',
    600: '#89CAE6',
    700: '#7AB4CC',
    800: '#6B9EB3',
    900: '#5C8899',
  },

  // Sidebar colors
  sidebar: {
    background: '#01122e',
    hoverBackground: '#0c2d55',
    selectedBackground: '#113866',
    iconColor: '#C4D4F2',
    iconHoverColor: '#ffffff',
    dividerColor: '#1E3E8A',
  },

  // Opacity tokens for state layers
  opacity: {
    hover: 0.04,
    enabledFill: 0.06,
    hoverFill: 0.09,
    selected: 0.08,
    focus: 0.12,
    focusVisible: 0.3,
    disabled: 0.38,
    disabledBackground: 0.12,
    outlinedBorder: 0.5,
    active: 0.56,
    defaultHoverFill: 0.12,
    defaultFocusFill: 0.2,
    backdrop: 0.5,
    overlay: 0.7,
    tooltipBackground: 0.95,
    // Dark mode variants (higher for visibility)
    hoverDark: 0.08,
    selectedDark: 0.16,
  },
} as const

export type Colors = typeof colors
export type ColorName = keyof Colors
export type ColorScale = Colors[ColorName]
